/**
 * DdlEditorShell — the outer shell every per-objectKind Visual DDL editor
 * mounts into. Ships two variants:
 *  - `'full'` (default): the workspace-tab shell — a header row (kind label
 *    + mono object name + optional header actions), an optional slim
 *    read-only hint line, a scrollable body, and an optional fixed-width
 *    sidebar rail.
 *  - `'compact'`: the stripped-down shell used inside the compact popover —
 *    just header actions (if any) and the body. No kind/name line, no
 *    sidebar, no read-only hint, no footer (the popover owns its own chrome).
 *
 * The old always-on `Alert` banner + footer button (formerly the standalone
 * `EditAsSqlEscape` helper, since retired) are gone: the full variant now
 * only renders the read-only hint (and its inline "Edit as SQL" link) when
 * the caller explicitly sets `readOnly`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice 0.4 — Read-only Render Scaffolding.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/
 */
import type { ReactNode } from 'react';

import { Button } from '../../../../../components/ui/button';

export type DdlEditorShellVariant = 'full' | 'compact';

export interface DdlEditorShellProps {
  /** Object kind label rendered in the header (e.g. `'View'`, `'Sequence'`). */
  readonly kindLabel: string;
  /** Object name (verbatim) rendered next to the kind label. May be `null`
   *  when the parsed buffer doesn't yet expose a name. */
  readonly objectName: string | null;
  /** Body slot — the per-kind editor's form / tabs. */
  readonly children: ReactNode;
  /** Required `data-testid` for the outer container — drives unit + E2E
   *  specs to locate the editor. */
  readonly testId: string;
  /** `'full'` = workspace tab (default), `'compact'` = inside the compact
   *  popover. */
  readonly variant?: DdlEditorShellVariant;
  /** Full variant: render the slim one-line read-only hint (replaces the
   *  old Alert). Ignored by the compact variant. */
  readonly readOnly?: boolean;
  /** Optional override for the read-only hint copy. */
  readonly bannerHint?: string;
  /** Read-only hint's "Edit as SQL" link target. Optional — full variant
   *  renders NO footer button anymore; compact renders no footer either
   *  (the popup owns it). */
  readonly onEditAsSql?: () => void;
  /** Optional slot rendered to the right of the header line (full variant) /
   *  as its own right-aligned row (compact variant) — used by e.g. the Table
   *  Designer to surface object-lifecycle actions (rename, move to schema). */
  readonly headerActions?: ReactNode;
  /** Full variant only — compact ignores it. Fixed-width rail rendered
   *  alongside the body, e.g. the dependency-annotation sidebar. */
  readonly sidebar?: ReactNode;
}

const DEFAULT_READONLY_HINT =
  'Read-only view — use “Edit as SQL” to change anything this panel doesn’t cover.';

export function DdlEditorShell({
  kindLabel,
  objectName,
  children,
  testId,
  variant = 'full',
  readOnly = false,
  bannerHint,
  onEditAsSql,
  headerActions,
  sidebar,
}: DdlEditorShellProps) {
  if (variant === 'compact') {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid={testId}
        data-object-name={objectName ?? undefined}
      >
        {headerActions ? (
          <div
            className="flex items-center justify-end gap-1"
            data-testid={`${testId}-header-actions`}
          >
            {headerActions}
          </div>
        ) : null}
        {children}
      </div>
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden"
      data-testid={testId}
      data-object-name={objectName ?? undefined}
    >
      <div className="flex items-baseline gap-x-3 px-4 pt-3 pb-2">
        <span
          className="text-xs uppercase tracking-wide text-muted-foreground"
          data-testid={`${testId}-kind-label`}
        >
          {kindLabel}
        </span>
        <span
          className="font-mono text-sm font-medium text-foreground"
          data-testid={`${testId}-object-name`}
        >
          {objectName ?? '(unnamed)'}
        </span>
        {headerActions ? (
          <div
            className="ml-auto flex items-center gap-1"
            data-testid={`${testId}-header-actions`}
          >
            {headerActions}
          </div>
        ) : null}
      </div>

      {readOnly ? (
        <div className="flex items-center gap-2 px-4 pb-2 text-xs text-muted-foreground">
          <span data-testid={`${testId}-banner-hint`}>{bannerHint ?? DEFAULT_READONLY_HINT}</span>
          {onEditAsSql ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-xs"
              onClick={onEditAsSql}
              data-testid={`${testId}-edit-as-sql`}
            >
              Edit as SQL
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-auto px-4 pb-4">{children}</div>
        {sidebar ? (
          <aside
            // Vertical scroll only: the rail's content (dependency pip,
            // mini-graph) is laid out to fit its fixed width and truncates
            // long names, so a sideways scrollbar would only ever expose
            // clipped padding.
            className="w-64 shrink-0 overflow-x-hidden overflow-y-auto border-l border-border px-3 py-3"
            data-testid={`${testId}-sidebar`}
          >
            {sidebar}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
